import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Generic bulk-selection state for a list/table view.
 *
 * Highlights:
 *  - Stable across page changes / filter changes — selections that
 *    aren't on the visible row set get auto-pruned (so you can't
 *    "submit" rows the user can't see anymore).
 *  - **Shift-click range selection.** Setting a checkbox while
 *    holding Shift sets every row between the previously-clicked
 *    row and this one (inclusive) to the same value. Rows are matched
 *    by their position in the `rows` array passed in. Behaves like
 *    the Gmail / Finder pattern.
 *  - Toggle-all-on-page checkbox returns an "indeterminate" state
 *    when only some rows on the visible page are selected.
 *
 * Generic over T extends { id: number } — works for tickets, orgs,
 * users, anything keyed by a numeric primary id.
 *
 * `setChecked` requires the *next* checked state explicitly (which
 * native checkboxes give us via `e.target.checked`). Computing it
 * inside the state updater would break under React 18 strict-mode
 * double-invocation — the updater would flip the value twice and the
 * net effect would be a no-op.
 */
export interface BulkSelection {
  /** The current set of selected row ids. */
  selected: Set<number>;
  /** Convenience helper for row rendering. */
  isSelected: (id: number) => boolean;
  /** Selected count. */
  count: number;

  /**
   * Set one row's selected state.
   *
   * If `opts.shiftKey` is true and there's a previous anchor, every
   * row between the anchor and `id` is set to `nextChecked`. The
   * `id` becomes the anchor for the next shift-click.
   */
  setChecked: (
    id: number,
    nextChecked: boolean,
    opts?: { shiftKey?: boolean },
  ) => void;

  /** Set every visible row to `checked`. */
  toggleAllOnPage: (checked: boolean) => void;

  /** Drop every selection. */
  clear: () => void;

  /** True when every row on the visible page is selected. */
  allOnPageChecked: boolean;

  /** True when some — but not all — rows on the visible page are selected. */
  someOnPageChecked: boolean;
}

export function useBulkSelection<T extends { id: number }>(
  rows: T[],
): BulkSelection {
  const [selected, setSelected] = useState<Set<number>>(() => new Set());

  // Last-clicked anchor for range-select. Re-set on every plain
  // (non-shift) click so shift-click always extends from the most
  // recent intentional pick. Cleared by toggleAllOnPage / clear.
  const anchorIdRef = useRef<number | null>(null);

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);

  // Drop selections that aren't on the visible row set anymore (after
  // pagination / filter changes). Also invalidates the anchor if its
  // row scrolled off, so the next shift-click acts as a single set.
  useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (visibleIds.includes(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
    if (
      anchorIdRef.current !== null &&
      !visibleIds.includes(anchorIdRef.current)
    ) {
      anchorIdRef.current = null;
    }
  }, [visibleIds]);

  const setChecked = useCallback<BulkSelection["setChecked"]>(
    (id, nextChecked, opts) => {
      const ids = visibleIds;
      const idx = ids.indexOf(id);
      if (idx === -1) return;

      // Snapshot the anchor *before* mutating, so the state updater
      // sees the right value even if React invokes it more than once
      // (strict-mode double-invocation in dev).
      const anchorAtCallTime = anchorIdRef.current;

      setSelected((prev) => {
        const next = new Set(prev);

        if (
          opts?.shiftKey &&
          anchorAtCallTime !== null &&
          anchorAtCallTime !== id
        ) {
          const anchorIdx = ids.indexOf(anchorAtCallTime);
          if (anchorIdx !== -1) {
            const [from, to] =
              anchorIdx < idx ? [anchorIdx, idx] : [idx, anchorIdx];
            for (let i = from; i <= to; i++) {
              const rid = ids[i]!;
              if (nextChecked) next.add(rid);
              else next.delete(rid);
            }
            return next;
          }
        }

        if (nextChecked) next.add(id);
        else next.delete(id);
        return next;
      });

      // Anchor always advances to the most recent click so the next
      // shift-click extends from here.
      anchorIdRef.current = id;
    },
    [visibleIds],
  );

  const toggleAllOnPage = useCallback<BulkSelection["toggleAllOnPage"]>(
    (checked) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (checked) for (const id of visibleIds) next.add(id);
        else for (const id of visibleIds) next.delete(id);
        return next;
      });
      anchorIdRef.current = null;
    },
    [visibleIds],
  );

  const clear = useCallback<BulkSelection["clear"]>(() => {
    setSelected(new Set());
    anchorIdRef.current = null;
  }, []);

  const allOnPageChecked =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someOnPageChecked =
    !allOnPageChecked && visibleIds.some((id) => selected.has(id));

  const isSelected = useCallback(
    (id: number) => selected.has(id),
    [selected],
  );

  return {
    selected,
    isSelected,
    count: selected.size,
    setChecked,
    toggleAllOnPage,
    clear,
    allOnPageChecked,
    someOnPageChecked,
  };
}
